import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const ChipsContainer = styled(motion.div)`
  display: flex;
  gap: 8px;
  padding: 4px 0 12px 44px;
  flex-wrap: wrap;

  ${props => props.$isMobile && `
    padding-left: 0;
    justify-content: center;
  `}
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(66, 133, 244, 0.08);
    border-color: rgba(66, 133, 244, 0.3);
    color: #4285F4;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg { width: 12px; height: 12px; }
`;

const Spinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(66, 133, 244, 0.2);
  border-top-color: #4285F4;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const NudgeChips = ({ dayIndex, timeBlock, activities, onMoreLikeThis, onSomethingDifferent, isMobile }) => {
  const [loadingAction, setLoadingAction] = useState(null);

  const lastActivity = activities?.[activities.length - 1];
  if (!lastActivity) return null;

  const handleMoreLike = async () => {
    if (loadingAction) return;
    setLoadingAction('more');
    try {
      await onMoreLikeThis(dayIndex, timeBlock, lastActivity);
    } catch { /* error handled upstream */ }
    setLoadingAction(null);
  };

  const handleDifferent = async () => {
    if (loadingAction) return;
    setLoadingAction('different');
    try {
      await onSomethingDifferent(dayIndex);
    } catch { /* error handled upstream */ }
    setLoadingAction(null);
  };

  return (
    <ChipsContainer
      $isMobile={isMobile}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Chip onClick={handleMoreLike} disabled={!!loadingAction}>
        {loadingAction === 'more' ? <Spinner /> : (
          <svg viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 1z"/></svg>
        )}
        More like this
      </Chip>
      <Chip onClick={handleDifferent} disabled={!!loadingAction}>
        {loadingAction === 'different' ? <Spinner /> : (
          <svg viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M8 2.5a5.487 5.487 0 00-4.131 1.869l1.204 1.204A.25.25 0 014.896 6H1.25A.25.25 0 011 5.75V2.104a.25.25 0 01.427-.177l1.38 1.38A7.001 7.001 0 0115 8a.75.75 0 01-1.5 0A5.5 5.5 0 008 2.5zM2.5 8a.75.75 0 00-1.5 0 7.001 7.001 0 0012.193 4.693l1.38 1.38a.25.25 0 00.427-.177V10.25a.25.25 0 00-.25-.25h-3.646a.25.25 0 00-.177.427l1.204 1.204A5.5 5.5 0 012.5 8z" clipRule="evenodd"/></svg>
        )}
        Something different
      </Chip>
    </ChipsContainer>
  );
};

export default NudgeChips;
